import React from "react";
import { Shield, Lightbulb, TrendingUp, Users, Check, Globe, Megaphone, Brain, Palette, Settings, GraduationCap } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { CTASection } from "../components/sections/CTASection";

const FOUNDER_IMG = "/founder-rudrik-joshi.jpg";

const PILLAR_ICONS = [Shield, Lightbulb, TrendingUp, Users];
const APPROACH_ICONS = [Globe, Megaphone, Brain, Palette, Settings, GraduationCap];

export default function AboutPage() {
  const { t, locale } = useI18n();
  useSeo({
    title: locale === "de"
      ? "Über uns — Rudrik Joshi & Joshi FusionTech Media | Digitalagentur Berlin"
      : "About — Rudrik Joshi & Joshi FusionTech Media | Digital Agency Berlin",
    description: locale === "de"
      ? "Lerne Rudrik Joshi und die Vision hinter Joshi FusionTech Media kennen — Digitalagentur aus Berlin für KI-Automatisierung und moderne Technologie."
      : "Meet Rudrik Joshi and the vision behind Joshi FusionTech Media — a Berlin-based digital agency for AI automation and modern technology.",
    canonical: "https://www.joshifusiontech.de/about",
    locale,
  });

  return (
    <main data-testid="about-page" className="pt-32">
      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-brand-orange" />
              <span className="label-mono">{t.about.kicker}</span>
            </div>
            <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl tracking-tight text-white leading-[1.0]">
              {t.about.title}
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/65 text-lg leading-relaxed">{t.about.subtitle}</p>
            <p className="mt-5 font-mono text-xs tracking-[0.3em] uppercase text-brand-orange">
              Automate · Innovate · Elevate
            </p>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section data-testid="story-section" className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Founder portrait */}
          <div className="lg:col-span-5">
            <div className="glass-strong rounded-3xl overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-azure/30 via-transparent to-brand-orange/20 opacity-60 pointer-events-none z-10" />
              <img
                src={FOUNDER_IMG}
                alt="Rudrik Joshi — Founder & Inhaber, Joshi FusionTech Media"
                width="640"
                height="640"
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover object-top aspect-square"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#05050A] via-[#05050A]/80 to-transparent z-20">
                <div className="label-mono text-brand-orange mb-1">[ inhaber ]</div>
                <div className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight">{t.about.founderName}</div>
                <div className="text-sm text-white/70 font-mono mt-1">{t.about.founderRole}</div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 font-mono text-xs text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" />
              Berlin · Germany
            </div>
          </div>

          {/* Story copy */}
          <div className="lg:col-span-7">
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              {t.about.storyTitle}
            </h2>
            <div className="mt-8 space-y-5 text-white/75 leading-relaxed text-base md:text-lg">
              {t.about.storyParas.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH GRID */}
      <section data-testid="approach-section" className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-12 h-px bg-brand-azureSoft" />
          <span className="label-mono">{t.about.approachTitle}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.about.approachItems.map((item, i) => {
            const Icon = APPROACH_ICONS[i] || Check;
            return (
              <div
                key={item}
                data-testid={`approach-${i}`}
                className="glass rounded-2xl p-6 flex items-start gap-4 hover:border-brand-orange/40 transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-brand-orange/15 border border-brand-orange/30 flex items-center justify-center shrink-0">
                  <Icon className="w-4.5 h-4.5 text-brand-orange" />
                </div>
                <div className="font-display font-bold text-base md:text-lg text-white pt-1.5">{item}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FOCUS BLOCK */}
      <section data-testid="focus-section" className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="glass-strong rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-brand-orange/15 blur-3xl" />
          <div className="relative z-10 max-w-4xl">
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              {t.about.focusTitle}
            </h2>
            <p className="mt-4 text-white/70 text-lg">{t.about.focusIntro}</p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-white/85">
              {t.about.focusItems.map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-brand-orange flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-base">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SLOGAN BAND */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="text-center">
          <p className="label-mono text-white/40 mb-4">{t.about.sloganLead}</p>
          <h2 className="font-display font-extrabold text-5xl md:text-7xl lg:text-8xl tracking-tight">
            <span className="text-white">Automate.</span>{" "}
            <span className="text-brand-orange">Innovate.</span>{" "}
            <span className="text-white">Elevate.</span>
          </h2>
          <p className="mt-8 text-white/65 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            {t.about.closing}
          </p>
        </div>
      </section>

      {/* PILLARS */}
      <section data-testid="pillars-section" className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="flex items-center gap-3 mb-10">
          <span className="w-12 h-px bg-brand-azureSoft" />
          <span className="label-mono">{t.about.pillarsTitle}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.about.pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <div
                key={p.t}
                data-testid={`pillar-${i}`}
                className="glass rounded-2xl p-7 hover:border-brand-orange/40 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-orange/15 border border-brand-orange/30 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-brand-orange" />
                </div>
                <h3 className="font-display font-bold text-xl text-white">{p.t}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      <CTASection />
    </main>
  );
}
